"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Loader from "@/components/Loader";
import { useTranslation } from "next-i18next";
import { toast } from "@/components/ui/use-toast";
import Link from "next/link";
import ViewModal from "@/components/blocks/flow/StrategyViewModal";
import SymbolUpdateModal from "@/components/common/SymbolUpdateModal";
import { Pagination, randomString } from "@/helpers/helperFunctions";
import { useRouter } from "next/router";
import { ExternalLinkIcon } from "lucide-react";
import { useUserContext } from "@/contexts/userContext";
import { useGetSymbolData } from "@/api/symbolApi";
import { useGetLogos, usePostCopyStrategy } from "@/api/strategyApi";
import { usePostMarketplaceFilterStrategies } from "@/api/marketPlaceApi";
import StrategyCard from "../cards/StrategyCard";
import { useAppDetails } from "@/contexts/AppDetailsContext";

const filters = {
  asset_class: "forex",
  sort_order: "app.trylevel2.com",
  win_p: "",
  SR: "",
  copiers: "",
  timeframe: "",
  sl_range: "",
  tp_range: "",
  exit: "",
  action: "",
};

const dataSize = 5;

export default function TrendingStrategies() {
  let [page, setPage] = useState(1);
  let [totalDataLength, setTotalDataLength] = useState(0);
  const [logos, setLogos] = useState<any>(new Map());
  const { t, tt } = {
    t: useTranslation("build-page").t,
    tt: useTranslation("marketplace-page").t,
  };

  const router = useRouter();
  const { user } = useUserContext();
  let [selected, setSelected] = useState({});
  let [showModal, setShowModal] = useState(false);
  let [showSymbolUpdateModal, setShowSymbolUpdateModal] = useState(false);
  let [currentCopyStrategy, setCurrentCopyStrategy] = useState(null);
  const { details } = useAppDetails();
  // API calls
  const {
    data,
    isLoading: isLoadingFilterStrategies,
    isSuccess: isSuccessFilterStrategies,
  } = usePostMarketplaceFilterStrategies(
    filters,
    String(page),
    String(dataSize),
  );
  const { mutateAsync: getLogo, data: logoData } = useGetLogos();

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
  } = usePostCopyStrategy();

  const { mutateAsync: getSymbolData, isPending: isCurrentPriceLoading } =
    useGetSymbolData();

  useEffect(() => {
    if (isSuccessFilterStrategies && data) {
      setTotalDataLength(data.data.total);

      if (data?.data) {
        let items = data?.data?.items;
        let finalData = items.map((each: any) => each?.strategy?.symbol);
        getLogo({ symbols: finalData });
      }
    }
  }, [isSuccessFilterStrategies, data]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user) {
      toast({ title: t("Strategy copied successfully") });

      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`,
        );
      }, 2000);
    }
  }, [isCopySuccess, copyStrategyData, user]);

  useEffect(() => {
    if (logoData) {
      setLogos((prev: {}) => ({
        ...prev,
        ...logoData?.data?.data, // merge new logo data into existing
      }));
    }
  }, [logoData]);

  const copyHandler = async (item: any) => {
    if (!user)
      return toast({ title: t("Login Required"), variant: "destructive" });

    try {
      const res = await getSymbolData({
        user_id: user?.user_id,
        ticker: item?.strategy.symbol,
        broker_id: details?.isWhiteLabled
          ? details?.id
          : item?.strategy.broker_id,
      });

      if (!res?.data || res?.data?.regularMarketPrice == 0) {
        setShowSymbolUpdateModal(true);
        setCurrentCopyStrategy(item?.strategy);
      } else {
        await copyStrategy({
          userId: user?.user_id,
          key: item?.strategy.keygen,
          symbol: item?.strategy.symbol,
        });
      }
    } catch (error) {
      toast({
        title: t("Error copying strategy") + error,
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
        token={user?.token}
      />
      <SymbolUpdateModal
        isOpen={showSymbolUpdateModal}
        strategy={currentCopyStrategy}
        setIsOpen={setShowSymbolUpdateModal}
        brokerSelectable={true}
      />

      <Card>
        <CardHeader className="flex !p-3  justify-between flex-row items-center w-full pb-0">
          <CardTitle className="text-xl flex gap-0 items-start text-nowrap flex-col">
            {tt("Top Strategies")}
          </CardTitle>

          <Link
            href="/marketplace"
            className="flex text-xs hover:underline text-[var(--accent-primary)] font-medium"
          >
            {tt("Explore More")} <ExternalLinkIcon className="h-[15px]" />
          </Link>
        </CardHeader>
        <p className="text-sm px-3 pb-3 text-muted-foreground font-medium">
          {tt("Find proven strategies to replicate")}
        </p>

        {isLoadingFilterStrategies ||
        (data && data?.data?.items.length) == 0 ? (
          <div className="w-full flex items-center justify-center h-[200px]">
            <Loader />
          </div>
        ) : (
          <CardContent className="p-3 pt-0 w-full h-[500px] overflow-auto">
            {data?.data?.items?.map((item: any, index: number) => (
              <StrategyCard
                key={index}
                item={item}
                index={index}
                logos={logos}
                isCurrentPriceLoading={isCurrentPriceLoading}
                isLoadingTicker={false}
                showExplain={page <= 3}
                onView={(val) => {
                  setSelected(val);
                  setShowModal(true);
                }}
                onExplain={(val) => {
                  setSelected(val);
                  setShowModal(true);
                }}
                onCopy={copyHandler}
                animate
              />
            ))}
            <div className="mt-3">
              <Pagination
                totalCount={totalDataLength}
                currentPage={page}
                changePage={setPage}
                limit={dataSize}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}
